import React, { type ReactNode } from 'react';
import { cn } from '../../utils/cn';
import { colors } from '../../tokens/colors';
import { transitions, reducedMotionCSS } from '../../utils/motion';

/* ── Card Root ── */

type CardVariant = 'default' | 'outlined' | 'elevated' | 'glass' | 'hero';

export interface CardProps {
  children: ReactNode;
  padding?: string;
  variant?: CardVariant;
  interactive?: boolean;
  className?: string;
}

const variantStyles: Record<CardVariant, React.CSSProperties> = {
  default: {
    background: colors.surface.card,
    border: `1px solid ${colors.border.default}`,
    boxShadow: '0 1px 2px rgba(0, 16, 36, 0.04)',
  },
  outlined: {
    background: colors.surface.card,
    border: `1px solid ${colors.border.default}`,
    boxShadow: 'none',
  },
  elevated: {
    background: colors.surface.card,
    border: 'none',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)',
  },
  glass: {
    background: 'rgba(255, 255, 255, 0.72)',
    border: `1px solid rgba(0, 32, 69, 0.15)`,
    boxShadow: '0 20px 40px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.9)',
    backdropFilter: 'blur(18px) saturate(155%)',
    WebkitBackdropFilter: 'blur(18px) saturate(155%)',
  },
  hero: {
    background: `linear-gradient(135deg, ${colors.primary[50]}, ${colors.surface.card})`,
    border: `1px solid ${colors.primary[200]}`,
    boxShadow: `0 22px 70px rgba(0,0,0,0.08), 0 0 80px ${colors.primary[200]}44`,
  },
};

export function Card({
  children,
  padding = '1rem',
  variant = 'default',
  interactive = false,
  className,
}: CardProps): JSX.Element {
  return (
    <div
      className={cn(
        'ds-card',
        `ds-card--${variant}`,
        interactive && 'ds-card--interactive',
        className,
      )}
      style={{
        position: 'relative',
        borderRadius: variant === 'glass' ? '12px' : '8px',
        padding,
        overflow: 'hidden',
        transition: `border-color ${transitions.normal}, box-shadow ${transitions.normal}, transform ${transitions.normal}`,
        ...variantStyles[variant],
      }}
    >
      {/* Hero glow overlay */}
      {variant === 'hero' && (
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            borderRadius: 'inherit',
            pointerEvents: 'none',
            background: `radial-gradient(circle at 30% 20%, ${colors.primary[200]}44, transparent 62%)`,
          }}
        />
      )}
      <div style={{ position: 'relative', zIndex: 1 }}>
        {children}
      </div>

      <style>{`
        .ds-card--interactive { cursor: pointer; }
        .ds-card--interactive:hover {
          border-color: ${colors.primary[500]} !important;
          box-shadow: 0 4px 12px rgba(0,0,0,0.08) !important;
          transform: translateY(-1px);
        }
        .ds-card--interactive:active { transform: translateY(0); }
        .ds-card--glass:hover {
          border-color: ${colors.primary[500]}66 !important;
          box-shadow: 0 20px 40px rgba(0,0,0,0.08), 0 0 40px ${colors.primary[200]}44 !important;
          transform: translateY(-2px);
        }
        .ds-card--hero:hover {
          box-shadow: 0 22px 70px rgba(0,0,0,0.12), 0 0 80px ${colors.primary[200]}66 !important;
        }
        .ds-card:focus-visible {
          outline: 2px solid ${colors.primary[500]};
          outline-offset: 2px;
        }
        ${reducedMotionCSS('.ds-card')}
      `}</style>
    </div>
  );
}

Card.displayName = 'Card';

/* ── Card Header ── */

export interface CardHeaderProps {
  children: ReactNode;
  className?: string;
  action?: ReactNode;
  /**
   * Faixa separada do corpo por uma linha, como o cabecalho de card do
   * DESIGN.md (`p-md border-b border-outline-variant`). Ligada por padrao:
   * nas 128 telas de referencia o cabecalho de card sempre tem a linha.
   */
  border?: boolean;
}

export function CardHeader({
  children,
  className,
  action,
  border = true,
}: CardHeaderProps): JSX.Element {
  return (
    <div
      className={cn('ds-card__header', className)}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '1rem',
        paddingBottom: border ? '1rem' : 0,
        marginBottom: '1rem',
        borderBottom: border ? `1px solid ${colors.border.default}` : 'none',
      }}
    >
      <div className="ds-card__header-content" style={{ flex: 1 }}>
        {children}
      </div>
      {action && (
        <div className="ds-card__header-action" style={{ flexShrink: 0 }}>
          {action}
        </div>
      )}
    </div>
  );
}

CardHeader.displayName = 'CardHeader';

/* ── Card Body ── */

export interface CardBodyProps {
  children: ReactNode;
  className?: string;
}

export function CardBody({ children, className }: CardBodyProps): JSX.Element {
  return (
    <div className={cn('ds-card__body', className)}>
      {children}
    </div>
  );
}

CardBody.displayName = 'CardBody';

/* ── Card Footer ── */

export interface CardFooterProps {
  children: ReactNode;
  className?: string;
  border?: boolean;
}

export function CardFooter({ children, className, border = true }: CardFooterProps): JSX.Element {
  return (
    <div
      className={cn('ds-card__footer', className)}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: '0.75rem',
        paddingTop: '1rem',
        marginTop: '1rem',
        borderTop: border ? `1px solid ${colors.border.default}` : 'none',
      }}
    >
      {children}
    </div>
  );
}

CardFooter.displayName = 'CardFooter';
